import type { ReactNode } from 'react';
import type { SocialProviderName } from '@redpandev/shared';
import { useSocialProviders } from './useSocialProviders';

/**
 * Boutons de connexion sociale sur /login et /signup (spec 11 §13), pilotés par
 * la sonde `GET /auth/providers` — **rien n'est rendu si la feature est dark**
 * (flags off → liste vide → prod intacte). Le DÉPART OAuth est une navigation
 * plein écran (`<a href>`) vers `/api/auth/:provider/start` (même origine :
 * proxy Vite en dev, nginx en prod), PAS un fetch : le provider répond un
 * redirect 302 que seul le navigateur peut suivre. `returnTo` est propagé.
 */

/** Glyphe Google 4 couleurs (charte officielle, SVG inline). */
function GoogleGlyph() {
  return (
    <svg viewBox="0 0 48 48" width="20" height="20" aria-hidden="true">
      <path
        fill="#EA4335"
        d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"
      />
      <path
        fill="#4285F4"
        d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"
      />
      <path
        fill="#FBBC05"
        d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"
      />
      <path
        fill="#34A853"
        d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"
      />
    </svg>
  );
}

interface ProviderMeta {
  label: string;
  glyph: ReactNode;
  /** Classes de marque (couleurs) — la forme pill vient de la base commune. */
  brand: string;
}

const PROVIDER_META: Partial<Record<SocialProviderName, ProviderMeta>> = {
  google: {
    label: 'Continuer avec Google',
    glyph: <GoogleGlyph />,
    brand:
      'bg-white text-[#1f1f1f] border border-black/10 hover:brightness-95 [[data-theme=light]_&]:shadow-sm',
  },
};

const PILL =
  'inline-flex min-h-[3.25rem] w-full select-none items-center justify-center gap-3 rounded-full px-7 text-base font-display font-semibold transition-[filter,box-shadow] duration-fast ease-fun';

function startUrl(provider: SocialProviderName, returnTo: string | null | undefined): string {
  const params = new URLSearchParams({ intent: 'login' });
  if (returnTo) params.set('returnTo', returnTo);
  return `/api/auth/${provider}/start?${params.toString()}`;
}

export function SocialAuthButtons({ returnTo }: { returnTo?: string | null }) {
  const providers = useSocialProviders();
  const usable = providers.filter((p) => PROVIDER_META[p]);
  if (usable.length === 0) return null;

  return (
    <div className="space-y-3">
      {/* Séparateur « ou » entre le formulaire classique et les providers. */}
      <div className="flex items-center gap-3 text-sm text-ink-400" aria-hidden="true">
        <span className="h-px flex-1 bg-ink-100/15" />
        ou
        <span className="h-px flex-1 bg-ink-100/15" />
      </div>
      {usable.map((p) => {
        const meta = PROVIDER_META[p]!;
        return (
          <a key={p} href={startUrl(p, returnTo)} className={`${PILL} ${meta.brand}`}>
            <span aria-hidden="true">{meta.glyph}</span>
            {meta.label}
          </a>
        );
      })}
    </div>
  );
}
